import { Emitter } from 'vue'
import { EventBuss } from './constants'

export class MJWebsocket {
    constructor(_emitter: Emitter) {
        this.emitter = _emitter

        if (typeof window !== 'undefined') {
            window.addEventListener('offline', this.handleOffline)
            window.addEventListener('online', this.handleOnline)
        }
    }

    ws!: WebSocket
    isConnected = false
    emitter!: Emitter

    heartBeatInterval = 10000
    reconnectInterval = 3000
    maxReconnectAttempts = 8
    heartbeatMissLimit = 3

    heartBeat: number | boolean = false

    OnMessage!: (event: MessageEvent) => void

    private url = ''
    private reconnectCount = 0
    private missCount = 0
    private generation = 0
    private reconnectTimer: number | boolean = false
    private allowReconnect = true
    private disconnected = false
    private hasConnectedOnce = false

    private handleOffline = (): void => {
        this.isConnected = false
        this.disconnected = true
        this.clearReconnectTimer()
        this.dropSocket()
        if (!this.hasConnectedOnce) return
        this.emitter.emit(EventBuss.NETWORK_OFFLINE)
        this.emitter.emit(EventBuss.SOCKET_DISCONNECTED)
    }

    private handleOnline = (): void => {
        this.emitter.emit(EventBuss.NETWORK_ONLINE)
        if (this.allowReconnect && this.url) {
            this.connect(this.url)
        }
    }

    connect(url: string): void {
        if (!url) return

        this.allowReconnect = true
        this.clearReconnectTimer()

        if (
            this.ws &&
            this.url === url &&
            (this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING)
        ) {
            this.isConnected = this.ws.readyState === WebSocket.OPEN
            return
        }

        this.dropSocket()
        this.url = url
        const generation = ++this.generation
        this.ws = new WebSocket(this.url)

        this.ws.onopen = () => {
            if (generation !== this.generation) return

            this.isConnected = true
            this.missCount = 0
            const wasReconnect = this.hasConnectedOnce
            this.hasConnectedOnce = true
            this.disconnected = false
            this.reconnectCount = 0
            this.bindMessageHandler(generation)
            this.startHeartBeat(generation)

            if (wasReconnect) {
                this.emitter.emit(EventBuss.SOCKET_RECONNECTED)
            }
        }

        this.bindMessageHandler(generation)

        this.ws.onclose = () => {
            if (generation !== this.generation) return

            this.isConnected = false
            this.disconnected = true
            this.stopHeartBeat()
            if (this.hasConnectedOnce) {
                this.emitter.emit(EventBuss.SOCKET_DISCONNECTED)
            }

            if (!this.allowReconnect) return
            this.scheduleReconnect()
        }

        this.ws.onerror = () => {
            if (generation !== this.generation) return
            this.isConnected = false
            if (
                this.ws &&
                this.ws.readyState !== WebSocket.CLOSING &&
                this.ws.readyState !== WebSocket.CLOSED
            ) {
                try {
                    this.ws.close()
                } catch {
                    // ignore close errors
                }
            }
        }
    }

    close(): void {
        this.allowReconnect = false
        this.clearReconnectTimer()
        this.isConnected = false
        this.hasConnectedOnce = false
        this.disconnected = false
        this.stopHeartBeat()
        this.dropSocket()
    }

    destroy(): void {
        this.close()
        this.url = ''
        if (typeof window !== 'undefined') {
            window.removeEventListener('offline', this.handleOffline)
            window.removeEventListener('online', this.handleOnline)
        }
    }

    private bindMessageHandler(generation: number): void {
        if (!this.ws) return
        this.ws.onmessage = (event: MessageEvent) => {
            if (generation !== this.generation) return
            this.missCount = 0
            if (typeof this.OnMessage === 'function') {
                this.OnMessage(event)
            }
        }
    }

    private startHeartBeat(generation: number): void {
        this.stopHeartBeat()
        this.missCount = 0
        this.heartBeat = window.setInterval(() => {
            if (generation !== this.generation) {
                this.stopHeartBeat()
                return
            }

            if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
                this.stopHeartBeat()
                return
            }

            this.missCount += 1
            if (this.missCount > this.heartbeatMissLimit) {
                try {
                    this.ws.close()
                } catch {
                    this.scheduleReconnect()
                }
                return
            }

            try {
                this.ws.send(
                    JSON.stringify({ type: 'client-ping', data: {} })
                )
            } catch {
                try {
                    this.ws.close()
                } catch {
                    this.scheduleReconnect()
                }
            }
        }, this.heartBeatInterval)
    }

    private scheduleReconnect(): void {
        this.clearReconnectTimer()
        if (!this.allowReconnect || !this.url) return
        if (typeof navigator !== 'undefined' && !navigator.onLine) return

        if (this.reconnectCount >= this.maxReconnectAttempts) {
            this.reconnectCount = 0
            this.emitter.emit(EventBuss.SOCKET_RECONNECT_FAILED)
            return
        }

        const delay = Math.min(
            this.reconnectInterval * Math.pow(2, this.reconnectCount),
            30000
        )
        this.reconnectCount += 1
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = false
            if (!this.allowReconnect || !this.url) return
            if (
                this.ws &&
                (this.ws.readyState === WebSocket.OPEN ||
                    this.ws.readyState === WebSocket.CONNECTING)
            ) {
                return
            }
            this.connect(this.url)
        }, delay)
    }

    private dropSocket(): void {
        this.generation += 1
        this.stopHeartBeat()
        if (!this.ws) return

        this.unregisterSocketHandlers(this.ws)
        if (
            this.ws.readyState !== WebSocket.CLOSING &&
            this.ws.readyState !== WebSocket.CLOSED
        ) {
            try {
                this.ws.close()
            } catch {
                // ignore
            }
        }
    }

    private clearReconnectTimer(): void {
        if (typeof this.reconnectTimer === 'number') {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = false
        }
    }

    private stopHeartBeat(): void {
        if (typeof this.heartBeat === 'number') {
            clearInterval(this.heartBeat)
            this.heartBeat = false
        }
    }

    private unregisterSocketHandlers(ws: WebSocket): void {
        ws.onmessage = null
        ws.onclose = null
        ws.onerror = null
        ws.onopen = null
    }
}
